import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faShield,
  faArrowLeft,
  faCheckCircle,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { burnoutAPI } from '../services/api';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './BurnoutWatch.css';

const BurnoutWatch = () => {
  const navigate = useNavigate();
  const [watchStatus, setWatchStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWatchStatus();
    // Poll every 30 seconds for updates
    const interval = setInterval(fetchWatchStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchWatchStatus = async () => {
    try {
      const response = await burnoutAPI.getWatchStatus();
      setWatchStatus(response.data);
    } catch (error) {
      console.error('Error fetching watch status:', error);
    } finally {
      setLoading(false);
    }
  };

  const runMonitoring = async () => {
    try {
      setLoading(true);
      const response = await burnoutAPI.getWatchStatus();
      setWatchStatus(response.data);
      toast.success('Protection check complete');
    } catch (error) {
      console.error('Error running protection check:', error);
      toast.error('Failed to run protection check');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !watchStatus) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div className="breathing-pulse">
          <FontAwesomeIcon icon={faShield} style={{ fontSize: '3rem', color: '#FFB6C1' }} />
        </div>
      </div>
    );
  }

  const getLevelColor = (level) => {
    switch (level) {
      case 'protecting':
        return '#FFB6C1'; // Rose
      case 'watching':
        return '#FFB6C1'; // Rose
      default:
        return '#10b981'; // Green for healthy
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      padding: '2rem'
    }}>
      <ToastContainer position="top-right" autoClose={3000} theme="dark" />
      
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        {/* Header */}
        <button 
          onClick={() => navigate('/dashboard')} 
          style={{
            background: 'none',
            border: '1px solid #FFB6C1',
            color: '#FFB6C1',
            padding: '0.5rem 1rem',
            borderRadius: '4px',
            cursor: 'pointer',
            marginBottom: '2rem'
          }}
        >
          <FontAwesomeIcon icon={faArrowLeft} /> Back to Dashboard
        </button>

        {/* Main Status Card */}
        <div style={{
          backgroundColor: '#1a1a1a',
          border: `2px solid ${getLevelColor(watchStatus?.level)}`,
          borderRadius: '16px',
          padding: '3rem',
          marginBottom: '2rem',
          textAlign: 'center'
        }}>
          <div className={watchStatus?.protected ? 'breathing-pulse' : ''}>
            <FontAwesomeIcon 
              icon={faShield} 
              style={{ 
                fontSize: '4rem', 
                color: '#FFB6C1',
                marginBottom: '1.5rem'
              }} 
            />
          </div>
          
          <h1 style={{ 
            fontSize: '2.5rem', 
            marginBottom: '1rem',
            color: '#FFB6C1'
          }}>
            Burnout Watch
          </h1>
          
          <p style={{ 
            fontSize: '1.1rem', 
            color: '#999',
            marginBottom: '2rem',
            fontStyle: 'italic'
          }}>
            Quiet Overload Protection
          </p>

          {/* Status Message */}
          <div style={{
            backgroundColor: '#000',
            border: `1px solid ${getLevelColor(watchStatus?.level)}`,
            borderRadius: '8px',
            padding: '1.5rem',
            marginBottom: '1.5rem'
          }}>
            <p style={{ 
              fontSize: '1.2rem',
              color: getLevelColor(watchStatus?.level),
              margin: 0,
              lineHeight: '1.6'
            }}>
              {watchStatus?.message || 'Loading...'}
            </p>
          </div>

          {/* Protection Indicators */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '1rem',
            marginTop: '2rem'
          }}>
            <ProtectionIndicator 
              label="Soft Lock"
              active={watchStatus?.soft_lock_active}
            />
            <ProtectionIndicator 
              label="Recovery Mode"
              active={watchStatus?.recovery_active}
            />
          </div>

          <button
            onClick={runMonitoring}
            disabled={loading}
            style={{
              marginTop: '2rem',
              padding: '1rem 2rem',
              backgroundColor: 'transparent',
              border: '2px solid #FFB6C1',
              borderRadius: '8px',
              color: '#FFB6C1',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.5 : 1,
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.target.style.backgroundColor = '#FFB6C1';
                e.target.style.color = '#000';
              }
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = '#FFB6C1';
            }}
          >
            {loading ? 'Checking...' : 'Run Protection Check'}
          </button>
        </div>

        {/* How It Works */}
        <div style={{
          backgroundColor: '#1a1a1a',
          border: '1px solid #333',
          borderRadius: '12px',
          padding: '2rem',
          marginBottom: '2rem'
        }}>
          <h3 style={{ 
            color: '#FFB6C1', 
            marginBottom: '1.5rem',
            fontSize: '1.5rem'
          }}>
            <FontAwesomeIcon icon={faInfoCircle} /> How Burnout Watch Protects You
          </h3>
          
          <div style={{ 
            color: '#ccc', 
            lineHeight: '1.8',
            fontSize: '1rem'
          }}>
            <p style={{ marginBottom: '1rem' }}>
              Burnout Watch runs quietly in the background, monitoring your wellbeing without alarms or red warnings.
            </p>
            
            <h4 style={{ color: '#FFB6C1', marginTop: '1.5rem', marginBottom: '1rem' }}>
              What it monitors:
            </h4>
            <ul style={{ paddingLeft: '1.5rem', margin: 0 }}>
              <li style={{ marginBottom: '0.5rem' }}>Tasks vs. your personal capacity</li>
              <li style={{ marginBottom: '0.5rem' }}>Repeated heavy days</li>
              <li style={{ marginBottom: '0.5rem' }}>Slow completion trends</li>
              <li style={{ marginBottom: '0.5rem' }}>Missing recovery time</li>
            </ul>

            <h4 style={{ color: '#FFB6C1', marginTop: '1.5rem', marginBottom: '1rem' }}>
              What it does:
            </h4>
            <ul style={{ paddingLeft: '1.5rem', margin: 0 }}>
              <li style={{ marginBottom: '0.5rem' }}>Soft-locks new tasks when you're at capacity</li>
              <li style={{ marginBottom: '0.5rem' }}>Activates Recovery Mode when needed</li>
              <li style={{ marginBottom: '0.5rem' }}>Lightens tomorrow's load automatically</li>
              <li style={{ marginBottom: '0.5rem' }}>Says "no" for you, reducing guilt and decision fatigue</li>
            </ul>
          </div>
        </div>

        {/* Visual Identity */}
        <div style={{
          backgroundColor: '#1a1a1a',
          border: '1px solid #333',
          borderRadius: '12px',
          padding: '2rem'
        }}>
          <h3 style={{ 
            color: '#FFB6C1', 
            marginBottom: '1rem',
            fontSize: '1.5rem'
          }}>
            Visual Identity
          </h3>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '2rem',
            color: '#ccc'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div className="breathing-pulse">
                <FontAwesomeIcon 
                  icon={faShield} 
                  style={{ fontSize: '3rem', color: '#FFB6C1' }} 
                />
              </div>
              <p style={{ marginTop: '1rem', fontSize: '0.9rem' }}>Shield Icon</p>
            </div>
            <div style={{ flex: 1 }}>
              <p style={{ marginBottom: '0.5rem' }}>
                <strong style={{ color: '#FFB6C1' }}>Color:</strong> Rose outline (#FFB6C1)
              </p>
              <p style={{ marginBottom: '0.5rem' }}>
                <strong style={{ color: '#FFB6C1' }}>Animation:</strong> Slow breathing pulse (when active)
              </p>
              <p style={{ marginBottom: 0 }}>
                <strong style={{ color: '#FFB6C1' }}>Meaning:</strong> Signals protection, not danger
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ProtectionIndicator = ({ label, active }) => {
  return (
    <div style={{
      backgroundColor: '#000',
      border: `1px solid ${active ? '#FFB6C1' : '#333'}`,
      borderRadius: '8px',
      padding: '1rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem'
    }}>
      <FontAwesomeIcon 
        icon={faCheckCircle} 
        style={{ 
          color: active ? '#FFB6C1' : '#666',
          fontSize: '1.2rem'
        }} 
      />
      <span style={{ 
        color: active ? '#FFB6C1' : '#999',
        fontWeight: active ? '600' : '400'
      }}>
        {label}
      </span>
    </div>
  );
};

export default BurnoutWatch;
